import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

/**
 * Lead Form — direct inquiry intake.
 * Card layout matching the user's spec: max-width 520px, dark background,
 * 4 fields (Name · Email · Company · Message), single primary CTA.
 * Styled with brand brass + Cormorant + Open Sans typography for consistency.
 */
export default function Lead() {
  const [params] = useSearchParams();
  const utm = useMemo(() => {
    const out = {};
    UTM_KEYS.forEach((k) => { out[k] = params.get(k) || ""; });
    return out;
  }, [params]);

  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatus("Sending…");
    try {
      await api.post("/leads", {
        ...form,
        ...utm,
        source: "lead_form",
        referrer: typeof document !== "undefined" ? (document.referrer || "") : "",
        landing_path: typeof window !== "undefined" ? (window.location.pathname + window.location.search) : "",
      });
      setStatus("Thank you. Your inquiry has been submitted.");
      setDone(true);
      toast.success("Inquiry received.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      setStatus("Something went wrong. Please try again.");
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="lead-page"
      className="newsletter-theme min-h-[100vh] flex items-center justify-center px-6 pt-28 pb-12"
      style={{ background: "var(--bg)" }}
    >
      <div
        className="w-full"
        style={{
          maxWidth: 520,
          background: "var(--surface)",
          border: "1px solid var(--hairline-strong)",
          borderRadius: 18,
          padding: "32px 28px",
          boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
        }}
      >
        {/* Logo + Heading */}
        <div className="text-center mb-7">
          <p className="newsletter-eyebrow mb-3" style={{ color: "var(--brass)" }}>
            WEALTH FIRST INC
          </p>
          <h1 className="newsletter-display text-4xl md:text-5xl mb-3 leading-[0.95]">
            Lead <em style={{ color: "var(--brass)" }}>Form</em>
          </h1>
          <p className="newsletter-body text-sm" style={{ color: "var(--ink-muted)", lineHeight: 1.55 }}>
            Request information, services, or a private consultation.
          </p>
        </div>

        {done ? (
          <SubmittedState message={status} onReset={() => { setDone(false); setStatus(""); }} />
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="lead-form">
            <Field label="Full Name *">
              <input
                required
                type="text"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className="lead-input"
                data-testid="lead-name"
                autoComplete="name"
              />
            </Field>
            <Field label="Email *">
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="lead-input"
                data-testid="lead-email"
                autoComplete="email"
              />
            </Field>
            <Field label="Company">
              <input
                type="text"
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                className="lead-input"
                data-testid="lead-company"
                autoComplete="organization"
              />
            </Field>
            <Field label="Message *">
              <textarea
                required
                minLength={4}
                rows={5}
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                className="lead-input"
                style={{ resize: "vertical", minHeight: 120 }}
                data-testid="lead-message"
              />
            </Field>

            <button
              type="submit"
              disabled={busy}
              data-testid="lead-submit"
              className="w-full"
              style={{
                marginTop: 18,
                padding: "14px 18px",
                border: "1px solid var(--brass)",
                borderRadius: 12,
                background: "var(--brass)",
                color: "var(--ink-on-brass, #1a1410)",
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                cursor: busy ? "not-allowed" : "pointer",
                minHeight: 48,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                opacity: busy ? 0.7 : 1,
              }}
            >
              {busy ? "Submitting…" : "Submit Inquiry"}
              <ArrowRight size={14} />
            </button>

            <div
              className="newsletter-body text-center"
              data-testid="lead-status"
              style={{ minHeight: 20, fontSize: 13, color: "var(--ink-muted)", marginTop: 12 }}
            >
              {status}
            </div>
            <p
              className="newsletter-body text-center"
              style={{ fontSize: 11, color: "var(--ink-muted)", opacity: 0.7, marginTop: 6, letterSpacing: "0.1em" }}
            >
              Wealth First Inc.
            </p>
          </form>
        )}
      </div>

      {/* Field-level styling */}
      <style>{`
        .lead-input {
          width: 100%;
          box-sizing: border-box;
          background: var(--bg);
          color: var(--ink);
          border: 1px solid var(--hairline-strong);
          border-radius: 12px;
          padding: 14px 15px;
          font-size: 15px;
          font-family: 'Open Sans', sans-serif;
          outline: none;
          transition: border-color 150ms ease;
        }
        .lead-input:focus { border-color: var(--brass); }
        .lead-input::placeholder { color: var(--ink-faint); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label
        className="newsletter-eyebrow block mb-2"
        style={{ fontSize: 11, color: "var(--ink)", letterSpacing: "0.18em" }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function SubmittedState({ message, onReset }) {
  return (
    <div data-testid="lead-submitted" className="text-center py-2">
      <CheckCircle2 size={48} style={{ color: "var(--brass)", margin: "0 auto 1.25rem" }} />
      <p className="newsletter-eyebrow mb-3" style={{ color: "var(--brass)" }}>Inquiry Received</p>
      <h2 className="newsletter-display text-2xl mb-3 leading-tight">
        Thank you — a partner advisor will reach out shortly.
      </h2>
      <p className="newsletter-body text-sm mb-6" style={{ color: "var(--ink-muted)" }}>
        {message || "We have logged your inquiry and a partner advisor will reach out within one business day."}
      </p>
      <button
        type="button"
        onClick={onReset}
        className="text-xs uppercase tracking-[0.18em]"
        style={{
          border: "1px solid var(--hairline-strong)",
          color: "var(--ink-muted)",
          background: "transparent",
          padding: "10px 18px",
          cursor: "pointer",
        }}
        data-testid="lead-reset"
      >
        Submit another inquiry
      </button>
    </div>
  );
}
